import React, { useState } from 'react';
import PoliceDialog from './PoliceDialog';
import AlertDialog from './AlertDialog';
import { formatCurrency, CONFIG, LOCATIONS, calculateFine, calculateBribe, resolveBribe, rollRaidOutcome } from '../gameData';

function TravelSection({ player, setPlayer, advanceDay, gameEnded }) {
  const [showPoliceDialog, setShowPoliceDialog] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [policeEncounterData, setPoliceEncounterData] = useState(null);

  const showAlert = (message) => {
    setAlertMessage(message);
    setShowAlertDialog(true);
  };

  const handleTravel = (location) => {
    if (gameEnded) return;

    // advanceDay handles moving + regenerating the market for the destination.
    advanceDay(location);

    // Chance of a police encounter — costs scale with the cash you're carrying.
    if (Math.random() < CONFIG.policeChance) {
      setPoliceEncounterData({
        fine: calculateFine(player.money),
        bribe: calculateBribe(player.money),
      });
      setShowPoliceDialog(true);
    }
  };

  const handlePoliceChoice = (choice) => {
    const { fine, bribe } = policeEncounterData;
    setShowPoliceDialog(false);
    
    switch (choice) {
      case "1": {
        // Pay Fine: the cheaper cash hit, but they seize your stash. You pay
        // what you can (a % of your cash), never going into debt.
        const paid = Math.min(player.money, fine);
        setPlayer(prev => ({
          ...prev,
          money: Math.max(0, prev.money - fine),
          inventory: {},
        }));
        showAlert(`You paid a ${formatCurrency(paid)} fine and the cops seized your stash.`);
        break;
      }

      case "2": {
        // Bribe: pricier, but you keep your stash. You can borrow the shortfall
        // — but only up to the debt limit. Maxed out and short = the bribe fails
        // and they seize your stash.
        const result = resolveBribe(player.money, player.debt, bribe);
        if (result.type === 'failed') {
          setPlayer(prev => ({ ...prev, inventory: {} }));
          showAlert(`You couldn't scrape together the ${formatCurrency(bribe)} bribe — the cops seized your stash.`);
        } else if (result.type === 'borrowed') {
          setPlayer(prev => ({ ...prev, money: result.money, debt: result.debt }));
          showAlert(
            `You bribed the cop ${formatCurrency(bribe)} to keep your stash — you were short, ` +
            `so the loan shark covered ${formatCurrency(result.borrowed)} (added to your debt).`
          );
        } else {
          setPlayer(prev => ({ ...prev, money: result.money }));
          showAlert(`You greased the cop for ${formatCurrency(bribe)} and kept your stash.`);
        }
        break;
      }

      case "3":
        if (Math.random() < CONFIG.escapeChance) {
          showAlert("🏃 You escaped clean — no fine, no losses!");
        } else {
          // Caught: roll how bad the bust is. Assets are never touched.
          const outcome = rollRaidOutcome();
          showAlert(outcome.message);
          setPlayer(prev => {
            switch (outcome.type) {
              case "drugs":
                return { ...prev, inventory: {} };
              case "drugsHalfCash":
                return { ...prev, inventory: {}, money: Math.floor(prev.money / 2) };
              case "all":
              default:
                return { ...prev, inventory: {}, money: 0 };
            }
          });
        }
        break;

      default:
        break;
    }
  };

  return (
    <div>
      <h2>Travel</h2>
      <p className="section-hint">Traveling burns a day. Prices differ by borough — buy low, sell high.</p>
      <div className="travel-options">
        {LOCATIONS.map(loc => (
          <button
            key={loc.name}
            onClick={() => handleTravel(loc.name)}
            disabled={gameEnded || loc.name === player.location}
            title={loc.blurb}
          >
            {loc.name}
            <small>{loc.blurb}</small>
          </button>
        ))}
      </div>
      
      <PoliceDialog 
        show={showPoliceDialog}
        onChoice={handlePoliceChoice}
        fine={policeEncounterData?.fine}
        bribe={policeEncounterData?.bribe}
      />

      <AlertDialog
        show={showAlertDialog}
        message={alertMessage}
        onClose={() => setShowAlertDialog(false)}
      />
    </div>
  );
}

export default TravelSection; 